// Micro-SFX: procedurally-synthesized WebAudio blips — zero audio-asset bytes. Tier-4 (browser).
// NEVER imported by game.ts (would break headless tests); main.ts subscribes it to the event bus.
// Fully guarded: no-ops when muted, when AudioContext is missing (headless Playwright/SSR), or
// before the user's first gesture has unlocked the context. (TECHNICAL_DESIGN.md §13.)

type Wave = 'sine' | 'square' | 'triangle' | 'sawtooth';
interface Blip { freq: number; dur: number; wave: Wave; gain?: number; slide?: number }

const MUTE_KEY = 'wildergrove.muted';

// A tiny palette of one-shot voices. Kept terse — the whole point is ~1KB of code, no samples.
const VOICES: Record<string, Blip> = {
  attack:   { freq: 200, dur: 0.06, wave: 'square',   gain: 0.12, slide: -60 },
  bow:      { freq: 520, dur: 0.07, wave: 'triangle', gain: 0.12, slide: 220 },
  hit:      { freq: 150, dur: 0.05, wave: 'square',   gain: 0.16, slide: -40 },
  crit:     { freq: 660, dur: 0.10, wave: 'square',   gain: 0.18, slide: 180 },
  kill:     { freq: 110, dur: 0.14, wave: 'sawtooth', gain: 0.16, slide: -70 },
  harvest:  { freq: 320, dur: 0.05, wave: 'triangle', gain: 0.11, slide: -80 },
  craft:    { freq: 440, dur: 0.10, wave: 'triangle', gain: 0.13, slide: 120 },
  pickup:   { freq: 720, dur: 0.05, wave: 'sine',     gain: 0.10, slide: 90 },
  hurt:     { freq: 90,  dur: 0.16, wave: 'sawtooth', gain: 0.20, slide: -30 },
  levelup:  { freq: 523, dur: 0.12, wave: 'triangle', gain: 0.15, slide: 200 },
  loot:     { freq: 600, dur: 0.10, wave: 'triangle', gain: 0.14, slide: 140 },
  ui:       { freq: 420, dur: 0.03, wave: 'sine',     gain: 0.08 },
  equip:    { freq: 340, dur: 0.06, wave: 'square',   gain: 0.12, slide: -110 }, // a gear "snap into place"
};

// A short rising arpeggio for the big moments (legendary drop, boss down).
const FANFARE = [523, 659, 784, 1047];

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  muted = false;

  constructor() {
    try { this.muted = localStorage.getItem(MUTE_KEY) === '1'; } catch { /* storage off */ }
  }

  /** Call from the first user gesture (title-screen click) — browsers block audio before that. */
  unlock(): void {
    if (this.ctx || this.muted) return;
    try {
      const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.6;
      this.master.connect(this.ctx.destination);
    } catch { this.ctx = null; }
  }

  setMuted(m: boolean): void {
    this.muted = m;
    try { localStorage.setItem(MUTE_KEY, m ? '1' : '0'); } catch { /* storage off */ }
    if (m) { try { this.ctx?.suspend(); } catch { /* ignore */ } }
    else { this.unlock(); try { this.ctx?.resume(); } catch { /* ignore */ } }
  }

  private blip(v: Blip, whenOffset = 0): void {
    if (this.muted || !this.ctx || !this.master) return;
    try {
      const t = this.ctx.currentTime + whenOffset;
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      osc.type = v.wave;
      osc.frequency.setValueAtTime(v.freq, t);
      if (v.slide) osc.frequency.linearRampToValueAtTime(Math.max(30, v.freq + v.slide), t + v.dur);
      g.gain.setValueAtTime(v.gain ?? 0.12, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + v.dur);
      osc.connect(g); g.connect(this.master);
      osc.start(t); osc.stop(t + v.dur + 0.02);
    } catch { /* an audio hiccup must never touch gameplay */ }
  }

  /** Play a named one-shot voice. Unknown names are ignored. */
  play(name: keyof typeof VOICES): void {
    const v = VOICES[name];
    if (v) this.blip(v);
  }

  /** Rising arpeggio for legendary drops / boss kills. */
  fanfare(): void {
    if (this.muted || !this.ctx) return;
    FANFARE.forEach((f, i) => this.blip({ freq: f, dur: 0.12, wave: 'triangle', gain: 0.14 }, i * 0.08));
  }
}

/** Wire an Sfx instance to a game's event bus. Called once from main.ts (game stays audio-free). */
export function mountSfx(sfx: Sfx, game: { on(ev: string, fn: (p?: unknown) => void): void }): void {
  game.on('attack', (p) => sfx.play((p as { weapon?: string })?.weapon === 'bow' ? 'bow' : 'attack'));
  game.on('hit', (p) => sfx.play((p as { crit?: boolean })?.crit ? 'crit' : 'hit'));
  game.on('kill', (p) => (p as { boss?: boolean })?.boss ? sfx.fanfare() : sfx.play('kill'));
  game.on('harvest', () => sfx.play('harvest'));
  game.on('craft', () => sfx.play('craft'));
  game.on('pickup', () => sfx.play('pickup'));
  game.on('equip', () => sfx.play('equip'));
  game.on('player-hit', () => sfx.play('hurt'));
  game.on('levelup', () => sfx.play('levelup'));
  game.on('loot', (p) => (p as { legendary?: boolean; rarity?: number })?.legendary || (p as { rarity?: number })?.rarity! >= 3 ? sfx.fanfare() : sfx.play('loot'));
}
